import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { DatePicker } from 'primeng/datepicker';
import { TabsModule } from 'primeng/tabs';
import {
  Persona,
  createEmptyPersona,
  TIPO_NIT
} from 'src/app/domain/model/persona/persona';
import { PERSONA_MAX_LENGTHS } from 'src/app/common/persona-utils';
import { TIPOS_SEXO } from 'src/app/common/constants-ejb';
import { environment } from 'src/environments/environment';
import {
  identificacionKeypress,
  sanitizeIdentificacion,
  sanitizeText,
  sanitizeEmail,
  emailKeypress
} from 'src/app/common/input-utils';
import { DireccionesPersonaComponent } from './direcciones-persona.component';
import { ContactosPersonaComponent } from './contactos-persona.component';
import { BuscadorMunicipioComponent } from '../shared/buscadores/buscador-municipio.component';
import { BuscadorActividadEconomicaComponent } from '../shared/buscadores/buscador-actividad-economica.component';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import { MunicipioRepository } from 'src/app/domain/service/municipio/municipio-repository';
import { ActividadEconomica } from 'src/app/domain/model/actividad-economica/actividad-economica';
import { ActividadEconomicaRepository } from 'src/app/domain/service/actividad-economica/actividad-economica-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';

/**
 * Componente de diálogo para crear/editar personas
 * Extraído de personas-list para mejor mantenibilidad
 */
@Component({
  selector: 'app-persona-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DialogModule,
    DropdownModule,
    TagModule,
    RadioButtonModule,
    TooltipModule,
    DatePicker,
    TabsModule,
    DireccionesPersonaComponent,
    ContactosPersonaComponent,
    BuscadorMunicipioComponent,
    BuscadorActividadEconomicaComponent
  ],
  template: `
    <!-- Dialog Crear/Editar -->
    <p-dialog
      [(visible)]="visible"
      [header]="soloLectura ? 'Detalle Persona' : (editMode ? 'Editar Persona' : 'Nueva Persona')"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '54rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      [draggable]="false"
      (onHide)="onDialogHide()"
    >
      <!-- Tabs -->
      <p-tabs
        [value]="activeTabIndex()"
        (valueChange)="activeTabIndex.set(+$event)"
      >
        <!-- Tab Datos Básicos -->
        <p-tablist>
          <p-tab [value]="0">
            <i class="pi pi-user mr-2"></i>
            Datos Básicos
          </p-tab>
          @if (!soloLectura) {
            <p-tab [value]="1" [disabled]="!editMode">
              <i class="pi pi-map-marker mr-2"></i>
              Direcciones
            </p-tab>
            <p-tab [value]="2" [disabled]="!editMode">
              <i class="pi pi-users mr-2"></i>
              Contactos
            </p-tab>
          }
        </p-tablist>

        <p-tabpanels>
          <!-- Panel Datos Básicos -->
          <p-tabpanel [value]="0">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              <!-- Tipo Identificación -->
              <div class="field">
                <label
                  for="tipoId"
                  class="block text-sm font-medium text-gray-700 mb-1"
                >
                  Tipo Identificación *
                </label>
                <p-dropdown
                  id="tipoId"
                  [(ngModel)]="personaForm.cdtipo_identificacion"
                  [options]="tiposIdentificacion()"
                  optionLabel="descripcion"
                  optionValue="codigo"
                  placeholder="Seleccione..."
                  appendTo="body"
                  [disabled]="soloLectura"
                  [filter]="true"
                  filterBy="descripcion"
                  [ngClass]="{
                    'ng-invalid ng-dirty': isFieldInvalid(
                      personaForm.cdtipo_identificacion
                    )
                  }"
                  [style]="{ width: '100%' }"
                >
                </p-dropdown>
                @if (isFieldInvalid(personaForm.cdtipo_identificacion)) {
                  <small class="text-red-500"
                    >Seleccione un tipo de identificación</small
                  >
                }
              </div>

              <!-- Número Identificación -->
              <div class="field">
                <label
                  for="numId"
                  class="block text-sm font-medium text-gray-700 mb-1"
                >
                  Número Identificación *
                </label>
                <input
                  id="numId"
                  pInputText
                  [(ngModel)]="personaForm.nmidentificacion"
                  [readonly]="soloLectura"
                  [maxlength]="maxLengths.nmidentificacion"
                  (keypress)="onIdentificacionKeypress($event)"
                  (change)="
                    personaForm.nmidentificacion = sanitizeId(
                      personaForm.nmidentificacion
                    )
                  "
                  [ngClass]="{
                    'ng-invalid ng-dirty': isFieldInvalid(
                      personaForm.nmidentificacion
                    )
                  }"
                  class="w-full"
                />
                @if (isFieldInvalid(personaForm.nmidentificacion)) {
                  <small class="text-red-500"
                    >Ingrese el número de identificación</small
                  >
                }
              </div>

              <!-- Dígito Verificación (solo NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="digitoVerif"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Dígito Verificación *
                  </label>
                  <input
                    id="digitoVerif"
                    pInputText
                    [(ngModel)]="personaForm.cddigito_verificacion"
                    [readonly]="soloLectura"
                    [maxlength]="maxLengths.cddigito_verificacion"
                    (keypress)="onIdentificacionKeypress($event)"
                    (change)="
                      personaForm.cddigito_verificacion = sanitizeId(
                        personaForm.cddigito_verificacion
                      )
                    "
                    [ngClass]="{
                      'ng-invalid ng-dirty': isFieldInvalid(
                        personaForm.cddigito_verificacion
                      )
                    }"
                    class="w-full"
                  />
                  @if (isFieldInvalid(personaForm.cddigito_verificacion)) {
                    <small class="text-red-500"
                      >Ingrese el dígito de verificación</small
                    >
                  }
                </div>
              }

              <!-- Número Trabajadores (solo NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="numTrabajadores"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Número de Trabajadores
                  </label>
                  <p-inputNumber
                    id="numTrabajadores"
                    [(ngModel)]="personaForm.nmtrabajadores"
                    [disabled]="soloLectura"
                    [min]="0"
                    [max]="999999"
                    [showButtons]="false"
                    [useGrouping]="false"
                    inputStyleClass="w-full"
                  >
                  </p-inputNumber>
                </div>
              }

              <!-- Nombre Comercial (solo NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field col-span-2">
                  <label
                    for="nombreComercial"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nombre Comercial *
                  </label>
                  <input
                    id="nombreComercial"
                    pInputText
                    [(ngModel)]="personaForm.dsnombre_comercial"
                    [readonly]="soloLectura"
                    (input)="
                      personaForm.dsnombre_comercial = sanitizeText(
                        personaForm.dsnombre_comercial
                      )
                    "
                    [maxlength]="maxLengths.dsnombre_comercial"
                    [ngClass]="{
                      'ng-invalid ng-dirty': isFieldInvalid(
                        personaForm.dsnombre_comercial
                      )
                    }"
                    class="w-full"
                  />
                  @if (isFieldInvalid(personaForm.dsnombre_comercial)) {
                    <small class="text-red-500"
                      >Ingrese el nombre comercial</small
                    >
                  }
                </div>
              }

              <!-- Nombre Legal (solo NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field col-span-2">
                  <label
                    for="nombreLegal"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nombre Legal *
                  </label>
                  <input
                    id="nombreLegal"
                    pInputText
                    [(ngModel)]="personaForm.dsnombre_legal"
                    [readonly]="soloLectura"
                    (input)="
                      personaForm.dsnombre_legal = sanitizeText(
                        personaForm.dsnombre_legal
                      )
                    "
                    [maxlength]="maxLengths.dsnombre_legal"
                    [ngClass]="{
                      'ng-invalid ng-dirty': isFieldInvalid(
                        personaForm.dsnombre_legal
                      )
                    }"
                    class="w-full"
                  />
                  @if (isFieldInvalid(personaForm.dsnombre_legal)) {
                    <small class="text-red-500">Ingrese el nombre legal</small>
                  }
                </div>
              }

              <!-- Tipo Empresa (solo NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="tipoEmpresa"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Tipo Empresa
                  </label>
                  <p-dropdown
                    id="tipoEmpresa"
                    [(ngModel)]="personaForm.cdtipo_empresa"
                    [options]="tiposEmpresa"
                    optionLabel="descripcion"
                    optionValue="codigo"
                    placeholder="Seleccione..."
                    appendTo="body"
                    [disabled]="soloLectura"
                    [filter]="true"
                    filterBy="descripcion"
                    [showClear]="true"
                    [style]="{ width: '100%' }"
                  >
                  </p-dropdown>
                </div>
              }

              <!-- Tamaño Empresa (solo NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="tamanioEmpresa"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Tamaño Empresa
                  </label>
                  <p-dropdown
                    id="tamanioEmpresa"
                    [(ngModel)]="personaForm.cdtamanio_empresa"
                    [options]="tamaniosEmpresa"
                    optionLabel="descripcion"
                    optionValue="codigo"
                    placeholder="Seleccione..."
                    appendTo="body"
                    [disabled]="soloLectura"
                    [filter]="true"
                    filterBy="descripcion"
                    [showClear]="true"
                    [style]="{ width: '100%' }"
                  >
                  </p-dropdown>
                </div>
              }

              <!-- Actividad Económica (solo para NIT) -->
              @if (isPersonaJuridica()) {
                <div class="field col-span-2">
                  <label
                    for="actividadEconomica"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Actividad Económica
                  </label>
                  <div class="flex gap-2 min-w-0">
                    <input
                      id="actividadEconomica"
                      pInputText
                      [value]="getActividadEconomicaDisplay()"
                      [readonly]="true"
                      class="flex-1 min-w-0"
                      placeholder="Seleccione actividad económica..."
                    />
                    @if (!soloLectura) {
                      <button
                        pButton
                        type="button"
                        icon="pi pi-search"
                        class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                        pTooltip="Buscar Actividad Económica"
                        (click)="abrirBuscadorActividadEconomica()"
                      ></button>
                    }
                  </div>
                </div>
              }

              <!-- Primer Nombre (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="nombre1"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Primer Nombre *
                  </label>
                  <input
                    id="nombre1"
                    pInputText
                    [(ngModel)]="personaForm.dsnombre1"
                    [readonly]="soloLectura"
                    (input)="
                      personaForm.dsnombre1 = sanitizeText(
                        personaForm.dsnombre1
                      )
                    "
                    [maxlength]="maxLengths.dsnombre1"
                    [ngClass]="{
                      'ng-invalid ng-dirty': isFieldInvalid(
                        personaForm.dsnombre1
                      )
                    }"
                    class="w-full"
                  />
                  @if (isFieldInvalid(personaForm.dsnombre1)) {
                    <small class="text-red-500">Ingrese el primer nombre</small>
                  }
                </div>
              }

              <!-- Segundo Nombre (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="nombre2"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Segundo Nombre
                  </label>
                  <input
                    id="nombre2"
                    pInputText
                    [(ngModel)]="personaForm.dsnombre2"
                    [readonly]="soloLectura"
                    (input)="
                      personaForm.dsnombre2 = sanitizeText(
                        personaForm.dsnombre2
                      )
                    "
                    [maxlength]="maxLengths.dsnombre2"
                    class="w-full"
                  />
                </div>
              }

              <!-- Primer Apellido (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="apellido1"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Primer Apellido *
                  </label>
                  <input
                    id="apellido1"
                    pInputText
                    [(ngModel)]="personaForm.dsapellido1"
                    [readonly]="soloLectura"
                    (input)="
                      personaForm.dsapellido1 = sanitizeText(
                        personaForm.dsapellido1
                      )
                    "
                    [maxlength]="maxLengths.dsapellido1"
                    [ngClass]="{
                      'ng-invalid ng-dirty': isFieldInvalid(
                        personaForm.dsapellido1
                      )
                    }"
                    class="w-full"
                  />
                  @if (isFieldInvalid(personaForm.dsapellido1)) {
                    <small class="text-red-500"
                      >Ingrese el primer apellido</small
                    >
                  }
                </div>
              }

              <!-- Segundo Apellido (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="apellido2"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Segundo Apellido
                  </label>
                  <input
                    id="apellido2"
                    pInputText
                    [(ngModel)]="personaForm.dsapellido2"
                    [readonly]="soloLectura"
                    (input)="
                      personaForm.dsapellido2 = sanitizeText(
                        personaForm.dsapellido2
                      )
                    "
                    [maxlength]="maxLengths.dsapellido2"
                    class="w-full"
                  />
                </div>
              }

              <!-- Sexo (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="sexo"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Sexo
                  </label>
                  <p-dropdown
                    id="sexo"
                    [(ngModel)]="personaForm.cdsexo"
                    [options]="tiposSexo"
                    optionLabel="descripcion"
                    optionValue="codigo"
                    placeholder="Seleccione..."
                    appendTo="body"
                    [disabled]="soloLectura"
                    [style]="{ width: '100%' }"
                  >
                  </p-dropdown>
                </div>
              }

              <!-- Fecha Nacimiento (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="fechaNacimiento"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Fecha Nacimiento
                  </label>
                  <p-datepicker
                    id="fechaNacimiento"
                    [(ngModel)]="personaForm.fenacimiento"
                    dateFormat="yy/mm/dd"
                    [showIcon]="true"
                    [disabled]="soloLectura"
                    [maxDate]="maxFechaNacimiento"
                    appendTo="body"
                    inputStyleClass="w-full"
                  >
                  </p-datepicker>
                </div>
              }

              <!-- Email Personal (solo para persona natural - según legacy) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="email"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Personal
                  </label>
                  <input
                    id="email"
                    pInputText
                    type="email"
                    [(ngModel)]="personaForm.dsemail"
                    [readonly]="soloLectura"
                    (keypress)="emailKeypress($event)"
                    (input)="
                      personaForm.dsemail = sanitizeEmail(personaForm.dsemail)
                    "
                    [maxlength]="maxLengths.dsemail"
                    class="w-full"
                  />
                </div>
              }

              <!-- Municipio Nacimiento (solo para persona natural) -->
              @if (!isPersonaJuridica()) {
                <div class="field">
                  <label
                    for="municipioNacim"
                    class="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Municipio Nacimiento
                  </label>
                  <div class="flex gap-2 min-w-0">
                    <input
                      id="municipioNacim"
                      pInputText
                      [value]="getMunicipioNacimientoDisplay()"
                      [readonly]="true"
                      class="flex-1 min-w-0"
                    />
                    @if (!soloLectura) {
                      <button
                        pButton
                        icon="pi pi-search"
                        class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                        pTooltip="Buscar municipio"
                        (click)="abrirBuscadorMunicipio()"
                      ></button>
                    }
                  </div>
                </div>
              }

              <!-- Estado -->
              <div class="field">
                <label
                  for="estado"
                  class="block text-sm font-medium text-gray-700 mb-1"
                >
                  Estado
                </label>
                <p-dropdown
                  id="estado"
                  [(ngModel)]="personaForm.snactivo"
                  [options]="estadosOptions"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  [disabled]="soloLectura"
                  [style]="{ width: '100%' }"
                >
                </p-dropdown>
              </div>
            </div>
          </p-tabpanel>

          <!-- Panel Direcciones -->
          <p-tabpanel [value]="1">
            <div class="pt-4">
              @if (editMode && personaForm.cdpersona) {
                <app-direcciones-persona
                  [cdpersona]="personaForm.cdpersona"
                  [visible]="visible"
                >
                </app-direcciones-persona>
              } @else {
                <div class="text-center p-4 text-gray-500">
                  <i class="pi pi-info-circle text-2xl mb-2 block"></i>
                  Guarde la persona primero para agregar direcciones
                </div>
              }
            </div>
          </p-tabpanel>

          <!-- Panel Contactos -->
          <p-tabpanel [value]="2">
            <div class="pt-4">
              @if (editMode && personaForm.cdpersona) {
                <app-contactos-persona
                  [cdpersona]="personaForm.cdpersona"
                  [tipoPersona]="isPersonaJuridica() ? 'JURIDICA' : 'NATURAL'"
                  [visible]="visible"
                >
                </app-contactos-persona>
              } @else {
                <div class="text-center p-4 text-gray-500">
                  <i class="pi pi-info-circle text-2xl mb-2 block"></i>
                  Guarde la persona primero para agregar contactos
                </div>
              }
            </div>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            [disabled]="saving()"
            (onClick)="cancelar()"
          >
          </p-button>
          @if (activeTabIndex() === 0 && !soloLectura) {
            <p-button
              label="Guardar"
              icon="pi pi-check"
              [loading]="saving()"
              [disabled]="saving()"
              (onClick)="guardar()"
            >
            </p-button>
          }
        </div>
      </ng-template>
    </p-dialog>

    <!-- Buscador de Municipios -->
    <app-buscador-municipio
      (municipioSeleccionado)="onMunicipioSeleccionado($event)"
    >
    </app-buscador-municipio>

    <!-- Buscador de Actividades Económicas -->
    <app-buscador-actividad-economica
      (actividadSeleccionada)="onActividadEconomicaSeleccionada($event)"
    >
    </app-buscador-actividad-economica>
  `
})
export class PersonaDialogComponent implements OnInit, OnChanges {
  // Repositorios para cargar nombres
  private readonly municipioRepository = inject(MunicipioRepository);
  private readonly actividadEconomicaRepository = inject(
    ActividadEconomicaRepository
  );

  // ViewChild para los buscadores
  @ViewChild(BuscadorMunicipioComponent)
  buscadorMunicipio!: BuscadorMunicipioComponent;
  @ViewChild(BuscadorActividadEconomicaComponent)
  buscadorActividadEconomica!: BuscadorActividadEconomicaComponent;

  // Inputs
  @Input() visible = false;
  @Input() editMode = false;
  /**
   * Modo solo lectura ("Ver Detalle") — legacy:
   * `ModificarPersonaViewImpl.showLinksVerDetalle(false)`, invocado desde
   * `activity.verDetallePersona()` en Modificar Empleado/Crear Empleado/
   * Modificar Cliente/Crear Cliente. En el legado deshabilita TODOS los
   * campos, oculta los buscadores de Actividad Económica/Municipio y las
   * pestañas de Direcciones/Contactos, y deja solo el botón de cierre — no
   * hay botón Grabar. No confundir con `editMode=false` (que es "Nueva
   * Persona", con todos los campos editables).
   */
  @Input() soloLectura = false;
  @Input() persona: Persona | null = null;
  @Input() tiposEmpresa: CatalogoOption[] = [];
  @Input() tamaniosEmpresa: CatalogoOption[] = [];

  // Outputs
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<Persona>();
  @Output() onCancel = new EventEmitter<void>();

  // Estado interno
  personaForm: Persona = createEmptyPersona();
  activeTabIndex = signal(0);
  submitted = signal(false);
  saving = signal(false);

  // Nombre del municipio de nacimiento seleccionado
  municipioNacimientoNombre = signal('');
  cargandoMunicipio = signal(false);

  // Nombre de la actividad económica seleccionada
  actividadEconomicaNombre = signal('');

  // Inyección de repositorios
  private readonly catalogoRepository = inject(CatalogoRepository);

  // Opciones de dropdowns (cargados desde BD)
  tiposIdentificacion = signal<CatalogoOption[]>([]);
  tiposSexo = TIPOS_SEXO;
  maxLengths = PERSONA_MAX_LENGTHS;

  // Handlers de validación
  onIdentificacionKeypress = identificacionKeypress;
  sanitizeId = sanitizeIdentificacion;
  sanitizeText = sanitizeText;
  sanitizeEmail = sanitizeEmail;
  emailKeypress = emailKeypress;

  estadosOptions = [
    { label: 'Activo', value: 'S' },
    { label: 'Inactivo', value: 'N' }
  ];

  maxFechaNacimiento = new Date();

  /**
   * Inicializa el componente cargando catálogos desde BD
   */
  ngOnInit(): void {
    this.cargarTiposIdentificacion();
  }

  /**
   * Carga los tipos de identificación desde BD
   * Tabla: TPSC_TIPOS_IDENTIFICACION
   */
  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando tipos de identificación:', error);
      }
    }
  }

  /**
   * Detecta cambios en los inputs
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['persona'] && this.persona) {
      this.personaForm = { ...this.persona };

      // Convertir fecha si viene como string
      if (
        this.persona.fenacimiento &&
        typeof this.persona.fenacimiento === 'string'
      ) {
        this.personaForm.fenacimiento = new Date(this.persona.fenacimiento);
      }

      // Cargar nombre del municipio si existe
      if (this.persona.cdmunicipio_nacim) {
        this.cargarNombreMunicipio(this.persona.cdmunicipio_nacim);
      } else {
        this.municipioNacimientoNombre.set('');
      }

      // Cargar nombre de actividad económica si existe
      if (this.persona.cdactividad_economica) {
        this.cargarNombreActividadEconomica(this.persona.cdactividad_economica);
      } else {
        this.actividadEconomicaNombre.set('');
      }
    }

    if (changes['visible'] && this.visible && !this.editMode) {
      // Nueva persona
      this.personaForm = createEmptyPersona();
      this.municipioNacimientoNombre.set('');
      this.actividadEconomicaNombre.set('');
      this.submitted.set(false);
      this.activeTabIndex.set(0);
    }
  }

  /**
   * Indica si el tipo de identificación seleccionado es NIT
   */
  isPersonaJuridica(): boolean {
    return this.personaForm.cdtipo_identificacion === TIPO_NIT;
  }

  /**
   * Valida si un campo obligatorio está vacío
   */
  isFieldInvalid(
    value: string | null | undefined,
    isRequired: boolean = true
  ): boolean {
    if (!isRequired) return false;
    return this.submitted() && (!value || value.trim() === '');
  }

  /**
   * Valida si el formulario es válido
   */
  isFormValid(): boolean {
    const f = this.personaForm;
    const commonValid = !!(
      f.cdtipo_identificacion && f.nmidentificacion?.trim()
    );

    if (this.isPersonaJuridica()) {
      return (
        commonValid &&
        !!(
          f.cddigito_verificacion?.trim() &&
          f.dsnombre_comercial?.trim() &&
          f.dsnombre_legal?.trim()
        )
      );
    } else {
      return commonValid && !!(f.dsnombre1?.trim() && f.dsapellido1?.trim());
    }
  }

  /**
   * Prepara la persona para envío al backend
   */
  private preparePersonaForSave(): Persona {
    const persona = { ...this.personaForm };

    // Convertir fecha de Date a string yyyy-MM-dd
    if (persona.fenacimiento instanceof Date) {
      const date = persona.fenacimiento;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      persona.fenacimiento = `${year}-${month}-${day}`;
    }

    // Limpiar y ajustar campos según tipo de persona
    if (this.isPersonaJuridica()) {
      persona.dsnombre1 = persona.dsnombre_comercial || '';
      persona.dsnombre2 = undefined;
      persona.dsapellido1 = undefined;
      persona.dsapellido2 = undefined;
      persona.cdsexo = undefined;
      persona.fenacimiento = undefined;
    } else {
      const nombreCompleto = [
        persona.dsnombre1,
        persona.dsnombre2,
        persona.dsapellido1,
        persona.dsapellido2
      ]
        .filter(Boolean)
        .join(' ');
      persona.dsnombre_comercial = nombreCompleto || undefined;
      persona.dsnombre_legal = undefined;
      persona.cddigito_verificacion = undefined;
      persona.nmtrabajadores = undefined;
      persona.cdtipo_empresa = undefined;
      persona.cdtamanio_empresa = undefined;
    }

    return persona;
  }

  /**
   * Guarda la persona emitiendo el evento al padre
   */
  guardar(): void {
    this.submitted.set(true);

    if (!this.isFormValid()) {
      return;
    }

    this.saving.set(true);
    const personaToSave = this.preparePersonaForSave();
    this.onSave.emit(personaToSave);
  }

  /**
   * Marca el guardado como completado (llamar desde el padre)
   */
  finalizarGuardado(exito: boolean): void {
    this.saving.set(false);
    if (exito) {
      this.visible = false;
      this.visibleChange.emit(false);
    }
  }

  /**
   * Cancela y cierra el diálogo
   */
  cancelar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onCancel.emit();
  }

  /**
   * Maneja el evento de cierre del diálogo
   */
  onDialogHide(): void {
    this.visibleChange.emit(false);
  }

  // =============================================
  // BUSCADOR DE MUNICIPIO
  // =============================================

  abrirBuscadorMunicipio(): void {
    this.buscadorMunicipio.abrir();
  }

  onMunicipioSeleccionado(municipio: Municipio): void {
    this.personaForm.cdmunicipio_nacim = municipio.cdmunicipio;
    this.municipioNacimientoNombre.set(municipio.dsmunicipio);
  }

  getMunicipioNacimientoDisplay(): string {
    if (this.cargandoMunicipio()) {
      return 'Cargando...';
    }
    return this.municipioNacimientoNombre() || '';
  }

  private async cargarNombreMunicipio(codigo: string): Promise<void> {
    this.cargandoMunicipio.set(true);
    try {
      const municipio = await this.municipioRepository.getByCodigo(codigo);
      if (municipio) {
        this.municipioNacimientoNombre.set(municipio.dsmunicipio);
      }
    } catch (error) {
      if (!environment.production) {
        console.error('Error al cargar municipio:', error);
      }
    } finally {
      this.cargandoMunicipio.set(false);
    }
  }

  // =============================================
  // BUSCADOR DE ACTIVIDAD ECONÓMICA
  // =============================================

  abrirBuscadorActividadEconomica(): void {
    this.buscadorActividadEconomica.abrir();
  }

  onActividadEconomicaSeleccionada(actividad: ActividadEconomica): void {
    this.personaForm.cdactividad_economica = actividad.cdciiu;
    this.actividadEconomicaNombre.set(actividad.dsciiu);
  }

  getActividadEconomicaDisplay(): string {
    const nombre = this.actividadEconomicaNombre();
    const codigo = this.personaForm.cdactividad_economica;
    if (nombre && codigo) {
      return `${codigo} - ${nombre}`;
    }
    if (codigo) {
      return codigo;
    }
    return '';
  }

  private async cargarNombreActividadEconomica(codigo: string): Promise<void> {
    try {
      const actividad =
        await this.actividadEconomicaRepository.getByCodigo(codigo);
      if (actividad) {
        this.actividadEconomicaNombre.set(actividad.dsciiu);
      }
    } catch (error) {
      if (!environment.production) {
        console.error('Error al cargar actividad económica:', error);
      }
    }
  }
}
